import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import Typography from "@mui/material/Typography";
import {
  TableContainer,
  TablePagination,
  useMediaQuery,
  Box,
  Paper,
} from "@mui/material";
import SearchInput from "./SearchInput";
import ActionButton from "./ActionButton";
import QuestionListTable from "./QuestionListTable";
import DeleteQuestionListDialog from "./DeleteQuestionListDialog";
import DefaultQuestionListDialog from "./DefaultQuestionListDialog";
import { QuestionItem } from "../../Models/question";
import {
  useToggleArchiveStatus,
  usePaginatedQuestionLists,
  useDeleteQuestionList,
  useCopyQuestionList,
  useImportQuestionList,
  useShareQuestionList,
  useSetDefaultQuestionList,
  useExportQuestionList,
} from "../../Query/useQuestions";
import theme from "../../Constants/theme";
import { useTranslation } from "react-i18next";
import { downloadDocumentBlob } from "../../Helpers/documentHelper";
import colors from "../../Constants/colors";
import { useDebouncedCallback } from "../../Hooks/useDebouncedCallback";

const MyQuestions: React.FC = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [page, setPage] = useState(1);
  const rowsPerPage = 10;
  const [confirmationDialogOpen, setConfirmationDialogOpen] = useState(false);
  const [currentId, setCurrentId] = useState<number | null>(null);
  const [openDefaultDialog, setOpenDefaultDialog] = useState(false);
  const [selectedList, setSelectedList] = useState<QuestionItem | null>(null);
  const toggleArchiveMutation = useToggleArchiveStatus();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isLargeScreen = useMediaQuery(theme.breakpoints.between("sm", "lg"));
  const { t } = useTranslation("myQuestionLists");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("asc");
  const [sortField, setSortField] = useState<string>("name");
  const deleteMutation = useDeleteQuestionList();
  const copyMutation = useCopyQuestionList();
  const exportMutation = useExportQuestionList();
  const setDefaultMutation = useSetDefaultQuestionList();
  const importMutation = useImportQuestionList();
  const shareMutation = useShareQuestionList();

  const debouncedSearch = useDebouncedCallback((value: string) => {
    setDebouncedSearchTerm(value);
    setPage(1);
  }, 500);

  const handleSearchChange = (value: string) => {
    setSearchTerm(value);
    debouncedSearch(value);
  };

  const { data: questionListsResponse, isLoading: isListsLoading } =
    usePaginatedQuestionLists(
      page,
      rowsPerPage,
      debouncedSearchTerm,
      sortField,
      sortOrder,
      undefined
    );

  const sourceList: QuestionItem[] =
    questionListsResponse?.question_lists ?? [];

  const handleSort = (field: string) => {
    const isAsc = sortField === field && sortOrder === "asc";
    setSortOrder(isAsc ? "desc" : "asc");
    setSortField(field);
  };

  const handleDelete = async (id: number) => {
    const shouldGoBack =
      (questionListsResponse?.question_lists?.length ?? 0) === 1 && page > 1;

    deleteMutation.mutate(id, {
      onSuccess: () => {
        if (shouldGoBack) {
          setPage(page - 1);
        }
      },
    });

    handleCloseDeleteDialog();
  };

  const handleEdit = (item: unknown) => {
    navigate(`/question_list`, {
      state: {
        item,
        action: "edit",
      },
    });
  };

  const handleCopy = async (item: QuestionItem) => {
    copyMutation.mutate(item.id, {
      onSuccess: (response) => {
        const newQuestionItem = sourceList.find(
          (questionList) => questionList.id === response.question_list_id
        );

        navigate(`/questions_list`, {
          state: {
            item: newQuestionItem,
            action: "copy",
          },
        });
      },
    });
  };

  const handleToggleArchive = async (id: number) => {
    toggleArchiveMutation.mutate(id);
  };

  const handleExportAdminList = async (id: number, name: string) => {
    exportMutation.mutate(
      { id, format: "json" },
      {
        onSuccess: (response) => {
          downloadDocumentBlob(response, `${name}.json`);
        },
      }
    );
  };

  const handleConfirmDefault = async () => {
    if (selectedList) {
      setDefaultMutation.mutate(
        {
          questionListId: selectedList.id,
          categoryId: selectedList.category.id,
        },
        {
          onSuccess: () => {
            setOpenDefaultDialog(false);
          },
        }
      );
    }
  };

  const handleImport = async (file: File) => {
    importMutation.mutate(file);
  };

  const handleShare = async (id: number) => {
    shareMutation.mutate(id);
  };

  const handleOpenDeleteDialog = (id: number) => {
    setCurrentId(id);
    setConfirmationDialogOpen(true);
  };

  const handleCloseDeleteDialog = () => {
    setConfirmationDialogOpen(false);
    setCurrentId(null);
  };

  return (
    <Box sx={{ minHeight: "calc(100vh - 64px)", bgcolor: colors.gray50 }}>
      <Box sx={{ maxWidth: "1280px", mx: "auto", px: 3, py: 4 }}>
        <Paper
          elevation={0}
          sx={{
            p: 3,
            mb: 3,
            borderRadius: 2,
            border: `1px solid ${colors.gray200}`,
            borderLeft: `4px solid ${colors.primary_dark}`,
          }}
        >
          <Typography
            variant="h5"
            sx={{
              fontWeight: 600,
              color: colors.primary_dark,
              mb: 2,
            }}
          >
            {t("myQuestionLists.pageTitle")}
          </Typography>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <SearchInput
              searchTerm={searchTerm}
              setSearchTerm={handleSearchChange}
            />
            <ActionButton
              onCreate={() => navigate(`/question_list`)}
              onImport={handleImport}
              isLoading={importMutation.isPending}
            />
          </Box>
        </Paper>

        <Paper
          elevation={0}
          sx={{
            p: 3,
            borderRadius: 2,
            border: `1px solid ${colors.gray200}`,
            borderLeft: `4px solid ${colors.primary_dark}`,
            mb: isMobile || isLargeScreen ? 11 : 0,
          }}
        >
          <TableContainer
            sx={{
              width: "100%",
              overflowX: "auto",
            }}
          >
            <QuestionListTable
              data={sourceList}
              sortField={sortField}
              sortOrder={sortOrder}
              handleSort={handleSort}
              onDelete={(id) => handleOpenDeleteDialog(id)}
              onEdit={handleEdit}
              onCopy={handleCopy}
              onToggleArchive={handleToggleArchive}
              onExport={handleExportAdminList}
              onShare={handleShare}
              onSetDefault={(item) => {
                setSelectedList(item);
                setOpenDefaultDialog(true);
              }}
              isLoading={isListsLoading}
            />
            <TablePagination
              rowsPerPageOptions={[]}
              component="div"
              count={questionListsResponse?.total ?? 0}
              page={page - 1}
              onPageChange={(_, newPage) => setPage(newPage + 1)}
              rowsPerPage={rowsPerPage}
              sx={{
                color: colors.primary_dark,
                "& .MuiTablePagination-displayedRows": {
                  color: colors.primary_dark,
                },
                "& .MuiTablePagination-toolbar": {
                  color: colors.primary_dark,
                },
              }}
            />
          </TableContainer>
        </Paper>

        <DeleteQuestionListDialog
          open={confirmationDialogOpen}
          onClose={handleCloseDeleteDialog}
          onConfirm={async () => {
            if (currentId) {
              await handleDelete(currentId);
              handleCloseDeleteDialog();
            }
          }}
        />

        <DefaultQuestionListDialog
          open={openDefaultDialog}
          onClose={() => setOpenDefaultDialog(false)}
          onConfirm={handleConfirmDefault}
          selectedList={selectedList}
        />
      </Box>
    </Box>
  );
};

export default MyQuestions;
